import React, { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase, refreshSession } from "../lib/supabase";
import { useToast } from "../components/ui/use-toast";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // Get current session first
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Session error:", sessionError);
          if (mounted) {
            setLoading(false);
            setUser(null);
            setSession(null);
          }
          return;
        }

        if (mounted && currentSession) {
          setSession(currentSession);
          setUser(currentSession.user);
          setLoading(false);
        }

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, currentSession) => {
            console.log("Auth state change:", event);
            
            if (event === 'TOKEN_REFRESHED') {
              console.log('Token refreshed successfully');
            }

            if (event === 'SIGNED_OUT') {
              // Clear any stored session data
              localStorage.removeItem('supabase.auth.token');
              if (mounted) {
                setSession(null);
                setUser(null);
              }
            } else if (currentSession && mounted) {
              setSession(currentSession);
              setUser(currentSession.user);
            }
            
            if (mounted) {
              setLoading(false);
            }
          }
        );

        return () => {
          mounted = false;
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error("Error initializing auth:", error);
        if (mounted) {
          setLoading(false);
          toast({
            variant: "destructive",
            title: "Authentication Error",
            description: "There was a problem with authentication. Please try signing in again.",
          });
        }
      }
    };

    initializeAuth();
  }, [toast]);

  const signOut = async () => {
    try {
      // Clear any stored tokens before signing out
      localStorage.removeItem('supabase.auth.token');
      
      const { error } = await supabase.auth.signOut({
        scope: 'local'
      });
      
      if (error) throw error;
      
      setUser(null);
      setSession(null);
      
      // Redirect to sign-in page
      window.location.href = '/sign-in';
    } catch (error: any) {
      console.error("Error signing out:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem signing out. Please try again.",
      });
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};